/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Button } from '../../../../atoms/Button';
import './PaginationStyles.scss';
const DEFAULT_LABELS = {
    page: 'Page',
    of: 'of',
    itemsPerPage: 'items per page',
    pageInputAriaLabel: 'Page',
    pageSizeAriaLabel: 'Page size',
    firstPage: 'First page',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    lastPage: 'Last page'
};
const baseClass = 'qmb-ui-table-pagination';
/**
 * Pagination Component
 * Listing pagination controls: first/prev/next/last buttons, an editable direct-page
 * input committed on Enter/blur, an optional page-size selector, and item range info.
 *
 * @version 2.0.1
 * @since 2026-02-18
 * @updated 2026-08-18 - Vertically center page input and page-size with buttons; 2rem left / 7rem right padding
 */
export function Pagination({ currentPage, totalPages, totalItems, pageSize, onPageChange, pageSizeOptions = [], onPageSizeChange, labels = {}, className }) {
    const l = Object.assign(Object.assign({}, DEFAULT_LABELS), labels);
    const [draftPage, setDraftPage] = useState(String(currentPage));
    const skipNextBlurRef = useRef(false);
    // Keep draft in sync when the controlled page changes externally
    useEffect(() => {
        setDraftPage(String(currentPage));
    }, [currentPage]);
    const firstItem = totalItems === 0 ? 0 : Math.min((currentPage - 1) * pageSize + 1, totalItems);
    const lastItem = Math.min(currentPage * pageSize, totalItems);
    const emitPageChange = (nextPage) => {
        const clamped = Math.min(Math.max(nextPage, 1), totalPages);
        if (clamped !== currentPage) {
            onPageChange(clamped);
        }
        setDraftPage(String(clamped));
    };
    const handleInputChange = (event) => {
        skipNextBlurRef.current = false;
        setDraftPage(event.target.value);
    };
    const handleInputKeyDown = (event) => {
        if (event.key !== 'Enter')
            return;
        const parsed = Number(draftPage);
        if (!Number.isInteger(parsed) || draftPage.trim() === '') {
            setDraftPage(String(currentPage));
            return;
        }
        event.preventDefault();
        skipNextBlurRef.current = true;
        emitPageChange(parsed);
    };
    const handleInputBlur = () => {
        if (skipNextBlurRef.current) {
            skipNextBlurRef.current = false;
            return;
        }
        const parsed = Number(draftPage);
        if (!Number.isInteger(parsed) || draftPage.trim() === '') {
            setDraftPage(String(currentPage));
            return;
        }
        emitPageChange(parsed);
    };
    const handlePageSizeChange = (event) => {
        const nextSize = Number(event.target.value);
        if (Number.isInteger(nextSize) && onPageSizeChange) {
            onPageSizeChange(nextSize);
        }
    };
    const showPageSizeSelector = pageSizeOptions.length > 0 && !!onPageSizeChange;
    return (React.createElement("div", { className: classNames(baseClass, className) },
        React.createElement("div", { className: `${baseClass}__nav` },
            React.createElement("div", { className: `${baseClass}__controls` },
                React.createElement(Button, { variant: "secondary", disabled: currentPage <= 1, onClick: () => emitPageChange(1), "aria-label": l.firstPage },
                    React.createElement("i", { className: "fa-light fa-angles-left", "aria-hidden": "true" })),
                React.createElement(Button, { variant: "secondary", disabled: currentPage <= 1, onClick: () => emitPageChange(currentPage - 1), "aria-label": l.previousPage },
                    React.createElement("i", { className: "fa-light fa-angle-left", "aria-hidden": "true" })),
                React.createElement("span", { className: `${baseClass}__page-input-group` },
                    React.createElement("span", null, l.page),
                    React.createElement("input", { className: `${baseClass}__page-input`, type: "number", min: 1, max: totalPages, value: draftPage, "aria-label": l.pageInputAriaLabel, onChange: handleInputChange, onKeyDown: handleInputKeyDown, onBlur: handleInputBlur }),
                    React.createElement("span", null,
                        l.of,
                        " ",
                        totalPages)),
                React.createElement(Button, { variant: "secondary", disabled: currentPage >= totalPages, onClick: () => emitPageChange(currentPage + 1), "aria-label": l.nextPage },
                    React.createElement("i", { className: "fa-light fa-angle-right", "aria-hidden": "true" })),
                React.createElement(Button, { variant: "secondary", disabled: currentPage >= totalPages, onClick: () => emitPageChange(totalPages), "aria-label": l.lastPage },
                    React.createElement("i", { className: "fa-light fa-angles-right", "aria-hidden": "true" }))),
            showPageSizeSelector && (React.createElement("span", { className: `${baseClass}__page-size` },
                React.createElement("select", { value: pageSize, "aria-label": l.pageSizeAriaLabel, onChange: handlePageSizeChange }, pageSizeOptions.map((size) => (React.createElement("option", { key: size, value: size }, size)))),
                React.createElement("span", null, l.itemsPerPage)))),
        React.createElement("span", { className: `${baseClass}__info`, "aria-live": "polite", "aria-atomic": "true" },
            firstItem,
            "\u2013",
            lastItem,
            " ",
            l.of,
            " ",
            totalItems)));
}
